import * as React from 'react';
import * as THREE from 'three';
import Water from './Water';
import Sky from './Sky';
import WebGLUtils from '../../utils/WebGLUtils';
import TextIntro from './TextIntro';
import Follow from './Follow';
import { mobileOperatingSystem } from '@utils/ViewPort';
import DateUtils from '@utils/Date';

const TinyAnimate = require ('TinyAnimate');
type TinyAnimateProps = {
    cancel: () => void;
}

interface Props {
  enthusiasmLevel?: number;
}

interface State {
    showTextIntro: boolean;
}

class Home extends React.Component<Props, State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private light: THREE.Light;
    private water: THREE.Water;
    private hours = new Date().getHours();
    private sunAnimate: TinyAnimateProps;
    private sunDayTimeout: number;
    private textIntroTimeout: number;
    private animationFrame: number;
    private isDay = DateUtils.isDay(this.hours);

    constructor(props: Props) {
        super(props);
        this.state = {
            showTextIntro: false,
        }
    }

    public componentDidMount() {

        if (!this.setContainer()) {
            return ;
        }

        this.setScene();

        this.addLight();
        this.addWater();
        this.addSky();

        const render = () => {
            (this.water.material as THREE.ShaderMaterialExtended).uniforms.time.value += 1.0 / 60.0;
            this.renderer.render( this.scene, this.camera );
        };

        const animate = () => {
            this.animationFrame = requestAnimationFrame( animate );
            render();
        };

        let timeout = 0;

        if (!mobileOperatingSystem || 'iOS' === mobileOperatingSystem) {
            animate();
            timeout = 2000;
        }

        this.textIntroTimeout = window.setTimeout(() => {
            this.setState({showTextIntro: true});
        }, timeout);
    }

    public componentWillUnmount() {
        window.removeEventListener( 'resize', this.onWindowResize, false );
        this.sunAnimate && this.sunAnimate.cancel();
        clearTimeout(this.sunDayTimeout);
        clearTimeout(this.textIntroTimeout);
        cancelAnimationFrame(this.animationFrame);
    }

    private addSky = () => {
        const shader = Sky.SkyShader;
        const material = new THREE.ShaderMaterial( {
            fragmentShader: shader.fragmentShader,
            vertexShader: shader.vertexShader,
            uniforms: THREE.UniformsUtils.clone( shader.uniforms ),
            side: THREE.BackSide
        } );
        const sky = new Sky(new THREE.BoxBufferGeometry( 1, 1, 1 ), material);
        sky.scale.setScalar( 10000 );
        this.scene.add( sky );

        const uniforms = (sky.material as THREE.ShaderMaterialSky).uniforms;
        uniforms.turbidity.value = 10;
        uniforms.rayleigh.value = 2;
        uniforms.luminance.value = 1;
        uniforms.mieCoefficient.value = 0.005;
        uniforms.mieDirectionalG.value = 0.8;

        const cubeCamera = new THREE.CubeCamera( 1, 20000, 256 );
        cubeCamera.renderTarget.texture.minFilter = THREE.LinearMipMapLinearFilter;

        const updateSun = (parameters: {distance: number, inclination: number, azimuth: number}) => {
            var theta = Math.PI * ( parameters.inclination - 0.5 );
            var phi = 2 * Math.PI * ( parameters.azimuth - 0.5 );
            this.light.position.x = parameters.distance * Math.cos( phi );
            this.light.position.y = parameters.distance * Math.sin( phi ) * Math.sin( theta );
            this.light.position.z = parameters.distance * Math.sin( phi ) * Math.cos( theta );
            (sky.material as THREE.ShaderMaterialSky).uniforms.sunPosition.value = this.light.position.copy( this.light.position );
            (this.water.material as THREE.ShaderMaterialExtended).uniforms.sunDirection.value.copy( this.light.position ).normalize();
            cubeCamera.updateCubeMap( this.renderer, this.scene );
        };

        const parameters = {
            distance: 400,
            inclination: this.isDay ? 0.509 : 0,
            azimuth: 0.25
        };

        if (this.isDay) {
            updateSun(parameters);
            this.sunDayTimeout = window.setTimeout(() => {
                this.sunAnimate = TinyAnimate.animate(0.509, 0, 4000,
                    (x: number) => {
                        parameters.inclination = x;
                        updateSun(parameters);
                    },
                    'easeInSine',
                );
            }, 2000);
        } else {
            this.sunAnimate = TinyAnimate.animate(0, 0.503, 4000,
                (x:number) => {
                    parameters.inclination = x;
                    updateSun(parameters);
                },
                'easeOutCirc',
            );
        }
    };

    private addWater = () => {
        this.water = new Water(
            new THREE.PlaneBufferGeometry( 10000, 10000 ),
            {
                waterNormals: new THREE.TextureLoader().load( '/water.jpg', (texture: THREE.Texture) => {
                    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
                }),
                alpha: 1.0,
                sunDirection: this.light.position.clone().normalize(),
                sunColor: 0xffffff,
                waterColor: 0x001e0f,
                distortionScale:  3.7,
                fog: this.scene.fog !== undefined
            }
        );
        this.water.rotation.x = - Math.PI / 2;
        this.scene.add(this.water);
    };

    private addLight = () => {
        this.light = new THREE.DirectionalLight( 0xffffff, 0.8 );
        this.scene.add( this.light );
    };

    private setScene = () => {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera( 55, window.innerWidth / window.innerHeight, 1, 20000 );
        this.camera.position.set( 30, 30, 100 );
    };

    private setContainer = (): boolean => {
        if (!this.containerRef || !this.containerRef.current) {
            return false;
        }
        if (WebGLUtils.isWebGLAvailable() === false) {
            this.containerRef.current.appendChild<HTMLDivElement>(WebGLUtils.getWebGLErrorMessage());
            return false;
        }
        this.renderer = new THREE.WebGLRenderer();
        this.renderer.setPixelRatio( window.devicePixelRatio );
        this.renderer.setSize( window.innerWidth, window.innerHeight );
        this.containerRef.current.appendChild<HTMLCanvasElement>(this.renderer.domElement);

        window.addEventListener('resize', this.onWindowResize, false);

        return true;
    };

    private onWindowResize = () => {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize( window.innerWidth, window.innerHeight );
    };

    private getGreeting = (): string => {
        if (this.hours >= 5 && this.hours < 12) {
            return "Good morning";
        } else if (this.hours >= 12 && this.hours < 18) {
            return "Good afternoon";
        }
        return "Good evening";

    };

    render(): JSX.Element {
        const greeting = this.getGreeting();
        const { showTextIntro } = this.state;

        return (
            <>
                { showTextIntro && 
                    <TextIntro
                        isDay={this.isDay}
                        greeting={greeting}
                    />
                }
                <div ref={this.containerRef} id="container" />
                <Follow />
            </>
        );
    }
}

export default Home;


// WEBPACK FOOTER //
// ./src/components/Home/index.tsx